import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}
interface State {
  hasError: boolean;
  message?: string;
}

/** Top-level error boundary. Fails safe: renders a contained error rather than a blank page. */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error.message };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // No PII / secrets in logs.
    console.error('Unhandled UI error', error.message, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="mx-auto mt-20 max-w-xl rounded-lg border border-red-500 bg-kore-card p-6" role="alert">
          <h2 className="text-lg font-semibold text-red-400">Something went wrong</h2>
          <p className="mt-2 text-sm text-slate-300">{this.state.message ?? 'An unexpected error occurred.'}</p>
          <button
            type="button"
            className="mt-4 rounded bg-kore-accent px-3 py-2 text-sm text-white hover:opacity-90"
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
